import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useParams, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import VendorOnboarding from "./Onboarding.jsx";
import fallbackSlotsConfig from "../../components/SlotsConfig_Summer2026";
import {
  TENT_SUBDIVISIONS, TENT_COLORS, TENT_LABELS, FACING_LABEL,
  getFacingBorders, getOrientedLayoutStyle, FacingArrow, CrownIcon, PencilIcon, TileSubPreview,
} from "../../components/Tentvisuals";
import { computeInvoice, POSITIONING_FEE, EXTRA_PRICES, TENT_PRICES, isExtraWaived } from "../../components/invoiceCalc";
import Pathway from "../../components/Pathway";
import "../../components/Pathway.css";
import {
  CheckCircle, Clock, Upload, MapPin, FileText, CreditCard,
  ArrowLeft, Download, RefreshCw, Layers, Megaphone, Sparkles,
} from "lucide-react";
import PaymentUploadModal from "./PaymentUploadModal";
import EventHero from "./EventHero";
import { getEventContent } from "./eventContent";
import "./VendorEventWorkspace.css";

const DOCS_BUCKET = "Global_Village_Vendors_2026";

const TENT_LABELS_APP = {
  "20x20_single": "20×20 Individual",
  "20x20_shared": "20×20 Shared",
  "10x10_single": "10×10 Individual",
  food_court:     "Food Court",
  pod_shared:     "17×17 Pod",
  "20x20_open":   "20×20 Open Space",
  sponsored:      "Biz Boost Program",
  ITC:            "ITC Space",
  WFTO:           "WFTO Space",
};

export const getSlotDisplayLabel = (rawSlotId, subId, tentMode, layoutItems = []) => {
  if (!rawSlotId) return "Not yet assigned";

  const clean = (val) => String(val || "").trim().toLowerCase();
  const conf = layoutItems.find((s) => clean(s.id) === clean(rawSlotId));
  const baseLabel = conf?.label || rawSlotId;

  if (subId && (tentMode === "20x20-double" || tentMode === "pod_shared" || tentMode === "17x17-pod")) {
    return `${baseLabel}${subId}`;
  }

  return baseLabel;
};

const ALLOWED_CONFIG_TYPES = {
  "20x20_single": ["20x20"],
  "20x20_shared": ["20x20"],
  "10x10_single": ["10x10-single"],
  pod_shared:     ["17x17-pod"],
  food_court:     ["Food Court"],
  "20x20_open":   ["20x20_open"],
  sponsored:      ["sponsored"],
  WFTO:           ["WFTO"],
  ITC:            ["ITC"],
};

const TABS = [
  { key: "overview",  label: "Overview",  icon: CheckCircle },
  { key: "invoice",   label: "Payment",   icon: CreditCard },
  { key: "layout",    label: "Layout",    icon: MapPin }, 
  { key: "documents", label: "Documents", icon: FileText },
  { key: "promote",   label: "Promote",   icon: Megaphone },
];

const getSteps = (app) => [
  { label: "Application approved", state: "done" },
  { label: "Deposit submitted",  state: app?.deposit_submitted ? "done" : "active" },
  { label: "Deposit confirmed",  state: app?.deposit_confirmed ? "done" : app?.deposit_submitted ? "active" : "pending" },
  { label: "Balance submitted",  state: app?.balance_submitted ? "done" : app?.deposit_confirmed ? "active" : "pending" },
  { label: "Balance confirmed",  state: app?.balance_confirmed ? "done" : app?.balance_submitted ? "active" : "pending" },
];

const VendorEventWorkspace = () => {
  const { vendor } = useOutletContext();
  const { eventKey } = useParams();

  const [event, setEvent] = useState(null);
  const [app, setApp] = useState(null);
  const [slot, setSlot] = useState(null);
  const [layoutItems, setLayoutItems] = useState(fallbackSlotsConfig);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [reclaimChoosingNew, setReclaimChoosingNew] = useState(false);

  useEffect(() => {
    if (!vendor?.id || !eventKey) return;

    const load = async () => {
      const { data: eventRow } = await supabase
        .from("events")
        .select("id, name, start_date, location, event_key")
        .or(`event_key.eq.${eventKey},id.eq.${eventKey}`)
        .maybeSingle();

      if (!eventRow) { setLoading(false); return; }
      setEvent(eventRow);

      const targetKey = eventRow.event_key || eventRow.id;

      // 1. Fetch dynamic layout definitions
      const { data: layoutData } = await supabase
        .from("layouts_published")
        .select("items")
        .eq("event_key", targetKey)
        .maybeSingle();

      if (layoutData?.items && Array.isArray(layoutData.items) && layoutData.items.length > 0) {
        setLayoutItems(layoutData.items);
      } else {
        setLayoutItems(fallbackSlotsConfig);
      }

      // 2. Fetch vendor application record
      const { data: appRow } = await supabase
        .from("vendor_applications")
        .select("*")
        .eq("vendor_id", vendor.id)
        .eq("event_id", eventRow.id)
        .maybeSingle();

      setApp(appRow || null);

      // 3. Fetch slot assignment
      if (appRow) {
        const { data: slotRow } = await supabase
          .from("vendor_slots")
          .select("slot_id, sub_id, tent_mode")
          .eq("vendor_application_id", appRow.id)
          .maybeSingle();
        setSlot(slotRow || null);
      }

      setLoading(false);
    };

    load();
  }, [vendor?.id, eventKey]);

  if (loading) return <div className="vw-loading">Loading event workspace…</div>;

  if (!event) {
    return (
      <div className="vw-notfound">
        <p>We couldn't find that event.</p>
        <Link to="/vendor/events" className="vw-back-link"><ArrowLeft size={14} /> Back to events</Link>
      </div>
    );
  }

  if (!app || !["approved", "accepted"].includes(String(app.status).toLowerCase())) {
    return (
      <div className="vw-notfound">
        <p>You don't have an approved application for {event.name}.</p>
        <Link to="/vendor/events" className="vw-back-link"><ArrowLeft size={14} /> Back to events</Link>
      </div>
    );
  }

  if (!app.onboarding_completed) {
    return (
      <VendorOnboarding
        vendor={vendor}
        event={event}
        app={app}
        onComplete={() => setApp((a) => ({ ...a, onboarding_completed: true }))}
      />
    );
  }

  const total = Math.round((app.invoice_total ?? app.custom_base_price ?? 0) || 0);
  const depositRequired = Math.round(app.deposit_required || total * 0.5);
  const balance = total - depositRequired;
  const needsReclaimDecision = app.status === "approved" && !!app.previous_chosen_spot && !app.chosen_spot;
  const content = getEventContent(event.event_key || event.id);

  const assignedSpotDisplay = slot
    ? getSlotDisplayLabel(slot.slot_id, slot.sub_id, slot.tent_mode, layoutItems)
    : app.chosen_spot
    ? getSlotDisplayLabel(app.chosen_spot, app.previous_sub_id, app.tent_type, layoutItems)
    : "Not yet assigned";

  return (
    <div className="vw-page">
      <Link to="/vendor/events" className="vw-back-link"><ArrowLeft size={14} /> All events</Link>

      <EventHero event={event} tagline={content.tagline} heroImage={content.heroImage} />

      {needsReclaimDecision ? (
        <div className="vw-backdrop">
          <div className="vw-modal vw-modal--reclaim" style={{ maxWidth: 720, width: "94%" }}>
            {reclaimChoosingNew ? (
              <ReclaimSpotPicker
                app={app}
                event={event}
                layoutItems={layoutItems}
                onBack={() => setReclaimChoosingNew(false)}
                onFinalized={(patch, newSlotObj) => {
                  setApp((a) => ({ ...a, ...patch }));
                  if (newSlotObj) setSlot(newSlotObj);
                  setReclaimChoosingNew(false);
                }}
              />
            ) : (
              <ReclaimDecisionCard
                app={app}
                event={event}
                layoutItems={layoutItems}
                onResolved={(patch, reclaimedSlotObj) => {
                  setApp((a) => ({ ...a, ...patch }));
                  if (reclaimedSlotObj) setSlot(reclaimedSlotObj);
                }}
                onChooseNew={() => setReclaimChoosingNew(true)}
              />
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="vw-stats">
            <div className="vw-stat">
              <MapPin size={16} />
              <div>
                <span>Your spot</span>
                <strong>{assignedSpotDisplay}</strong>
              </div>
            </div>
            <div className="vw-stat">
              <FileText size={16} />
              <div>
                <span>Space type</span>
                <strong>{TENT_LABELS_APP[app.tent_type] || app.tent_type || "—"}</strong>
              </div>
            </div>
          </div>

          <div className="vw-tabs" role="tablist">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={activeTab === t.key}
                  className={`vw-tab${activeTab === t.key ? " vw-tab--active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  <Icon size={15} />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="vw-tab-panel">
            {activeTab === "overview" && <OverviewTab app={app} event={event} content={content} />}
            {activeTab === "promote" && <PromoteTab event={event} content={content} />}
            {activeTab === "layout" && (
              <LayoutTab
                eventKey={event.event_key || event.id}
                application={app}
                mySlotId={slot?.slot_id}
                layoutItems={layoutItems}
              />
            )}
            {activeTab === "documents" && (
              <DocumentsTab app={app} vendor={vendor} event={event} onAppUpdate={setApp} />
            )}
            {activeTab === "extras" && (
              <ExtrasTab app={app} onAppUpdate={setApp} eventKey={event.event_key || event.id} />
            )}
            {activeTab === "invoice" && (
              <InvoiceTab
                app={app}
                vendor={vendor}
                event={event}
                total={total}
                depositRequired={depositRequired}
                balance={balance}
                onAppUpdate={setApp}
                layoutItems={layoutItems}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ── Reclaim decision card ────────────────────────────────────────────────
function ReclaimDecisionCard({ app, event, layoutItems, onResolved, onChooseNew }) {
  const [checking, setChecking] = useState(true);
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const eventKeyVal = event.event_key || event.id;
  const previousSubId = app.previous_sub_id || "A";

  const slotLabel = getSlotDisplayLabel(
    app.previous_chosen_spot,
    app.previous_sub_id,
    app.previous_tent_mode,
    layoutItems
  );

  const checkReclaimAvailability = async () => {
    const { data, error: fetchErr } = await supabase
      .from("vendor_slots")
      .select("id, slot_id, sub_id, tent_mode, vendor_application_id, reserved")
      .or(`event_key.eq.${eventKeyVal},event_key.eq.${event.id}`)
      .eq("slot_id", app.previous_chosen_spot);

    if (fetchErr) throw fetchErr;

    const previousMode = app.previous_tent_mode;

    if (!data || data.length === 0) {
      return { available: true, reason: null };
    }

    if (data.some((row) => row.reserved)) {
      return { available: false, reason: "reserved" };
    }

    if (previousMode === "20x20-single") {
      return { available: false, reason: "tile_taken" };
    }

    if (previousMode === "20x20-double") {
      if (data.some((row) => row.tent_mode === "20x20-single")) {
        return { available: false, reason: "configuration_changed" };
      }

      if (data.some((row) => row.tent_mode && row.tent_mode !== "20x20-double")) {
        return { available: false, reason: "configuration_changed" };
      }

      const subTaken = data.some((row) => (row.sub_id || "A") === previousSubId);
      if (subTaken) {
        return { available: false, reason: "subslot_taken" };
      }

      return { available: true, reason: null };
    }

    const exactSubTaken = data.some((row) => (row.sub_id || "A") === previousSubId);
    return {
      available: !exactSubTaken,
      reason: exactSubTaken ? "subslot_taken" : null,
    };
  };

  useEffect(() => {
    let mounted = true;

    const check = async () => {
      setChecking(true);
      setError("");

      try {
        const result = await checkReclaimAvailability();
        if (mounted) setAvailable(result.available);
      } catch (err) {
        console.error("Reclaim availability check failed:", err);
        if (mounted) {
          setAvailable(false);
          setError("Unable to check whether your previous spot is available.");
        }
      } finally {
        if (mounted) setChecking(false);
      }
    };

    check();

    return () => {
      mounted = false;
    };
  }, [app.previous_chosen_spot, app.previous_sub_id, app.previous_tent_mode, eventKeyVal]);

  const clearStash = async (extraPatch = {}) => {
    const patch = {
      previous_chosen_spot: null,
      previous_sub_id: null,
      previous_tent_mode: null,
      ...extraPatch,
    };

    const { error: updErr } = await supabase
      .from("vendor_applications")
      .update(patch)
      .eq("id", app.id);

    if (updErr) throw updErr;
    return patch;
  };

  const handleReclaim = async () => {
    setBusy(true);
    setError("");

    try {
      const result = await checkReclaimAvailability();

      if (!result.available) {
        setAvailable(false);
        if (result.reason === "configuration_changed") {
          setError("Your previous spot is no longer available in its original configuration. Please choose a different spot below.");
        } else if (result.reason === "subslot_taken") {
          setError("Your previous side of the spot is already occupied. Please choose a different spot below.");
        } else if (result.reason === "reserved") {
          setError("Your previous spot has been reserved by the organiser. Please choose a different spot below.");
        } else {
          setError("Your previous spot is no longer available. Please choose a different spot below.");
        }
        return;
      }

      const slotRecord = {
        event_key: eventKeyVal,
        slot_id: app.previous_chosen_spot,
        sub_id: previousSubId,
        vendor_id: app.vendor_id,
        vendor_application_id: app.id,
        tent_mode: app.previous_tent_mode,
        self_selected: true,
      };

      const { error: insErr } = await supabase
        .from("vendor_slots")
        .insert(slotRecord);

      if (insErr) {
        const isDuplicate = insErr.code === "23505" || /duplicate key/i.test(insErr.message || "");
        if (isDuplicate) {
          setAvailable(false);
          setError("That spot was just taken by someone else — choose a different one below.");
          return;
        }
        throw insErr;
      }

      const { total, depositRequired } = computeInvoice({
        tentType: app.tent_type,
        customBasePrice: app.custom_base_price,
        extras: app.extras,
        hasChosenSpot: true,
        eventKey: eventKeyVal,
      });

      const patch = await clearStash({
        chosen_spot: app.previous_chosen_spot,
        invoice_total: total,
        deposit_required: depositRequired,
      });

      onResolved(patch, slotRecord);
    } catch (err) {
      console.error("Reclaim failed:", err);
      setError(err.message || "Unable to reclaim that spot.");
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    setBusy(true);
    setError("");

    try {
      const { total, depositRequired } = computeInvoice({
        tentType: app.tent_type,
        customBasePrice: app.custom_base_price,
        extras: app.extras,
        hasChosenSpot: false,
        eventKey: eventKeyVal,
      });

      const patch = await clearStash({
        invoice_total: total,
        deposit_required: depositRequired,
      });

      onResolved(patch, null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vw-card">
      <h3 className="vw-card-title">Your spot was held for you</h3>
      <p className="vw-muted">
        While your account was on hold, your previously self-selected spot ({slotLabel}) was released so other approved vendors could claim it.
        {checking
          ? " Checking whether it's still available…"
          : available
            ? " Good news — it's still open."
            : " It's since been taken by another vendor."}
      </p>
      {error && <p className="vw-error">{error}</p>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
        {!checking && available && (
          <button className="vw-btn vw-btn--primary" onClick={handleReclaim} disabled={busy}>
            {busy ? "Reclaiming…" : `Reclaim ${slotLabel}`}
          </button>
        )}
        <button className="vw-btn vw-btn--secondary" onClick={onChooseNew} disabled={busy}>
          Choose a different spot
        </button>
        <button className="vw-btn vw-btn--ghost" onClick={handleDecline} disabled={busy}>
          {busy ? "Saving…" : "Continue without a self-selected spot"}
        </button>
      </div>
    </div>
  );
}

// ── Reclaim: choose a new spot ───────────────────────────────────────────
function ReclaimSpotPicker({ app, event, layoutItems, onBack, onFinalized }) {
  const eventKeyVal = event.event_key || event.id;
  const appTentType = app.tent_type;
  const allowedTypes = ALLOWED_CONFIG_TYPES[appTentType] || [];
  const is20x20Vendor = appTentType === "20x20_single" || appTentType === "20x20_shared";
  const wantsSharing = appTentType === "20x20_shared";

  const [subBookings, setSubBookings] = useState({});
  const [reservedSlots, setReservedSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [zoomIndex, setZoomIndex] = useState(2);
  const ZOOM_LEVELS = [0.5, 0.65, 0.8, 1, 1.25, 1.5];

  const showMsg = (text, type = "info") => { setMessage(text); setMessageType(type); };

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("vendor_slots")
      .select("id, slot_id, sub_id, vendor_application_id, tent_mode, reserved, self_selected")
      .or(`event_key.eq.${eventKeyVal},event_key.eq.${event.id}`);

    if (error) { console.error("Slots fetch:", error); setLoading(false); return; }
    if (!data?.length) { setSubBookings({}); setReservedSlots([]); setLoading(false); return; }

    const appIds = [...new Set(data.filter((r) => r.vendor_application_id).map((r) => r.vendor_application_id))];
    let appNames = {};
    if (appIds.length > 0) {
      const { data: apps } = await supabase
        .from("vendor_applications")
        .select("id, vendors(brand_name)")
        .in("id", appIds);
      apps?.forEach((a) => { appNames[a.id] = a.vendors?.brand_name || "Unknown"; });
    }

    const map = {};
    const reserved = [];
    data.forEach((row) => {
      if (row.reserved) { reserved.push(row.slot_id); return; }
      const sub = row.sub_id || "A";
      const key = `${row.slot_id}:${sub}`;
      if (row.vendor_application_id) {
        map[key] = {
          vendorId: row.vendor_application_id,
          vendorName: appNames[row.vendor_application_id] || "Unknown",
          tent_mode: row.tent_mode,
          selfSelected: row.self_selected,
        };
      }
    });
    setSubBookings(map);
    setReservedSlots(reserved);
    setLoading(false);
  }, [eventKeyVal, event.id]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const getTileSubBookings = (slotId) => {
    const out = {};
    Object.entries(subBookings).forEach(([k, v]) => {
      if (k.startsWith(slotId + ":")) out[k.split(":")[1]] = v;
    });
    return out;
  };

  const getEffectiveType = (slotConf) => {
    if (reservedSlots.includes(slotConf.id)) return "reserved";
    if (slotConf.type === "20x20") {
      const tileSubs = getTileSubBookings(slotConf.id);
      const entries = Object.entries(tileSubs);
      if (entries.length) {
        const mode = entries[0][1].tent_mode;
        if (mode) return mode;
        const subsPresent = new Set(entries.map(([sub]) => sub));
        return (subsPresent.size > 1 || subsPresent.has("B")) ? "20x20-double" : "20x20-single";
      }
      if (is20x20Vendor) return wantsSharing ? "20x20-double" : "20x20-single";
      return "20x20";
    }
    return slotConf.type;
  };

  const getTileCap = (slotConf) => {
    const et = getEffectiveType(slotConf);
    return TENT_SUBDIVISIONS[et]?.subs.length ?? 1;
  };

  const isSlotEligible = (slotConf) => {
    if (slotConf.type === "blocker" || slotConf.type === "pathway" || slotConf.type === "space") return false;
    if (reservedSlots.includes(slotConf.id)) return false;
    if (!allowedTypes.includes(slotConf.type)) return false;
    if (is20x20Vendor) {
      const tileSubs = getTileSubBookings(slotConf.id);
      const existingMode = Object.values(tileSubs)[0]?.tent_mode;
      if (!existingMode) return true;
      if (existingMode === "20x20-single") return false;
      if (existingMode === "20x20-double") return wantsSharing;
    }
    return true;
  };

  const zoomIn = () => setZoomIndex((i) => Math.min(i + 1, ZOOM_LEVELS.length - 1));
  const zoomOut = () => setZoomIndex((i) => Math.max(i - 1, 0));

  const clean = (val) => String(val || "").trim().toLowerCase();
  const activeSlotConfig = selectedSlot
    ? layoutItems.find((s) => clean(s.id) === clean(selectedSlot))
    : null;

  const activeEffType = activeSlotConfig ? getEffectiveType(activeSlotConfig) : null;
  const activeTileSubs = selectedSlot ? getTileSubBookings(selectedSlot) : {};
  const activeIsReserved = selectedSlot ? reservedSlots.includes(selectedSlot) : false;
  const activeEligible = activeSlotConfig ? isSlotEligible(activeSlotConfig) : false;
  const activeDef = activeEffType ? TENT_SUBDIVISIONS[activeEffType] : null;
  const activeIsMultiSub = activeDef && activeDef.subs.length > 1;
  const activeOrientedStyle = activeDef ? getOrientedLayoutStyle(activeDef.layout, activeSlotConfig?.facing) : undefined;
  const claimTentMode = is20x20Vendor ? (wantsSharing ? "20x20-double" : "20x20-single") : activeEffType;

  const displaySlotLabel = activeSlotConfig?.label || selectedSlot;

  const handleConfirmClaim = async () => {
    if (!selectedSlot || !selectedSub) return;
    setBusy(true);
    showMsg("");
    try {
      const slotRecord = {
        event_key: eventKeyVal,
        slot_id: selectedSlot,
        sub_id: selectedSub,
        vendor_id: app.vendor_id,
        vendor_application_id: app.id,
        tent_mode: claimTentMode,
        self_selected: true,
      };

      const { error: insErr } = await supabase.from("vendor_slots").insert(slotRecord);

      if (insErr) {
        if (insErr.code === "23505") {
          showMsg("That spot was just taken by another vendor — pick another.", "error");
          await fetchSlots();
          setSelectedSub(null);
          return;
        }
        throw insErr;
      }

      const { total, depositRequired } = computeInvoice({
        tentType: app.tent_type,
        customBasePrice: app.custom_base_price,
        extras: app.extras,
        hasChosenSpot: true,
        eventKey: eventKeyVal,
      });

      const patch = {
        chosen_spot: selectedSlot,
        invoice_total: total,
        deposit_required: depositRequired,
        previous_chosen_spot: null,
        previous_sub_id: null,
        previous_tent_mode: null,
      };

      const { error: updErr } = await supabase.from("vendor_applications").update(patch).eq("id", app.id);
      if (updErr) throw updErr;

      onFinalized(patch, slotRecord);
    } catch (err) {
      showMsg("Failed to claim slot: " + err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vw-card vw-card--layout">
      <button className="vw-btn vw-btn--ghost" onClick={onBack} style={{ marginBottom: 12 }}>
        <ArrowLeft size={14} /> Back
      </button>

      <h3 className="vw-card-title"><MapPin size={16} /> Choose a new spot</h3>
      <p className="vw-muted">Tap an open spot to see details, then confirm to claim it. This replaces your released spot on your invoice.</p>

      {message && <div className={`vw-message vw-message--${messageType}`}>{message}</div>}

      <div className="vw-legend">
        {allowedTypes.map((type) => (
          <div key={type} className="vw-legend-item">
            <span className="vw-legend-dot" style={{ background: TENT_COLORS[type] || "#999" }} />
            <span>{TENT_LABELS[type] || type} — <em>your slots</em></span>
          </div>
        ))}
        <div className="vw-legend-item vw-legend-item--dim">
          <span className="vw-legend-dot" style={{ background: TENT_COLORS.reserved }} />
          <span>Reserved</span>
        </div>
      </div>

      {loading ? (
        <p className="vw-muted">Loading layout…</p>
      ) : (
        <div className="vw-grid-canvas">
          <div className="vw-zoom-controls">
            <button className="vw-zoom-btn" onClick={zoomOut} disabled={zoomIndex === 0} aria-label="Zoom out">−</button>
            <button className="vw-zoom-btn vw-zoom-btn--label" onClick={() => setZoomIndex(2)}>
              {Math.round(ZOOM_LEVELS[zoomIndex] * 100)}%
            </button>
            <button className="vw-zoom-btn" onClick={zoomIn} disabled={zoomIndex === ZOOM_LEVELS.length - 1} aria-label="Zoom in">+</button>
          </div>

          <div className="vw-grid-scroll">
            <div className="vw-grid-transform" style={{ transform: `scale(${ZOOM_LEVELS[zoomIndex]})` }}>
              <div className="vw-layout-grid">
                {layoutItems.map((slotConf) => {
                  if (slotConf.type === "pathway") return <Pathway key={slotConf.id} slot={slotConf} />;

                  if (slotConf.type === "blocker") {
                    const isEntry = slotConf.id === "Entry";
                    const isExit = slotConf.id === "Exit";
                    return (
                      <div
                        key={slotConf.id}
                        className={[
                          "vw-layout-blocker",
                          isEntry ? "vw-layout-blocker--entry" : "",
                          isExit ? "vw-layout-blocker--exit" : "",
                          !isEntry && !isExit ? "vw-layout-blocker--gap" : "",
                        ].filter(Boolean).join(" ")}
                        style={{ gridColumn: slotConf.gridColumn, gridRow: slotConf.gridRow }}
                      >
                        {isEntry ? "Entry" : isExit ? "Exit" : ""}
                      </div>
                    );
                  }

                  if (slotConf.id === "P0") {
                    return (
                      <div key="P0" className="vw-layout-podium" style={{ gridColumn: slotConf.gridColumn, gridRow: slotConf.gridRow }}>
                        PODIUM
                      </div>
                    );
                  }

                  const et = getEffectiveType(slotConf);
                  const tileSubs = getTileSubBookings(slotConf.id);
                  const used = Object.keys(tileSubs).length;
                  const cap = getTileCap(slotConf);
                  const isRes = reservedSlots.includes(slotConf.id);
                  const eligible = isSlotEligible(slotConf);
                  const isPod = et === "17x17-pod";
                  const hasFacing = slotConf.facing && !isRes;
                  const full = used >= cap;
                  const isClickable = eligible && !isRes && !full;

                  return (
                    <div
                      key={slotConf.id}
                      className={[
                        "vw-layout-tile",
                        isPod ? "vw-layout-tile--pod" : "",
                      ].filter(Boolean).join(" ")}
                      style={{
                        gridColumn: slotConf.gridColumn,
                        gridRow: slotConf.gridRow,
                        backgroundColor: TENT_COLORS[et] || "#d9d9d9",
                        opacity: !eligible && !isRes ? 0.35 : 1,
                        cursor: isClickable ? "pointer" : "not-allowed",
                        ...(hasFacing ? getFacingBorders(slotConf.facing) : {}),
                      }}
                      onClick={() => { if (!isRes) { showMsg(""); setSelectedSlot(slotConf.id); setSelectedSub(null); } }}
                    >
                      {used > 0 && !isRes && <div className="vw-layout-tile-overlay" style={{ opacity: (used / cap) * 0.35 }} />}
                      <strong className="vw-layout-tile-label">{slotConf.label || slotConf.id}</strong>

                      <TileSubPreview slotId={slotConf.id} subBookings={subBookings} effectiveType={et} facing={slotConf.facing} />

                      {!isPod && used === 1 && !isRes && cap === 1 && (
                        <span className="vw-layout-tile-initial">{Object.values(tileSubs)[0]?.vendorName?.[0]?.toUpperCase()}</span>
                      )}
                      {cap > 1 && !isRes && <small className="vw-layout-tile-count">{used}/{cap}</small>}
                      {hasFacing && <FacingArrow facing={slotConf.facing} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSlot && activeSlotConfig && (
        <div className="vw-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setSelectedSlot(null); }}>
          <div className="vw-modal">
            <div className="vw-modal-header">
              <h4>{displaySlotLabel}</h4>
              <button className="vw-modal-close" onClick={() => setSelectedSlot(null)}>✕</button>
            </div>

            {activeIsReserved ? (
              <p className="vw-muted">Reserved by the organiser.</p>
            ) : !activeEligible ? (
              <p className="vw-muted">This slot type doesn't match your space type.</p>
            ) : (
              <>
                <div className="vw-modal-meta">
                  <span
                    className="vw-badge"
                    style={{ background: (TENT_COLORS[activeEffType] || "#999") + "22", color: TENT_COLORS[activeEffType] || "#999" }}
                  >
                    {TENT_LABELS[activeEffType] || activeEffType}
                  </span>
                  {activeSlotConfig.facing && <span className="vw-badge vw-badge--gray">{FACING_LABEL[activeSlotConfig.facing]}</span>}
                </div>

                {activeDef && (
                  <div className="vw-floorplan">
                    <div
                      className={`vw-floorplan-grid vw-floorplan-grid--${activeDef.layout}`}
                      style={activeOrientedStyle}
                    >
                      {activeDef.subs.map((sub) => {
                        const booking = activeTileSubs[sub];
                        const isSelected = selectedSub === sub;
                        const subLabel = activeIsMultiSub ? `${displaySlotLabel}${sub}` : displaySlotLabel;
                        const canPick = !booking;
                        return (
                          <div
                            key={sub}
                            className={[
                              "vw-sub-slot",
                              booking ? "vw-sub-slot--filled" : "vw-sub-slot--empty",
                              isSelected ? "vw-sub-slot--selected" : "",
                            ].filter(Boolean).join(" ")}
                            style={{
                              background: TENT_COLORS[activeEffType] || "#ccc",
                              cursor: canPick ? "pointer" : "default",
                            }}
                            onClick={() => { if (canPick) setSelectedSub((prev) => (prev === sub ? null : sub)); }}
                          >
                            <span className="vw-sub-slot-id">{subLabel}</span>
                            {booking ? (
                              <>
                                <div className="vw-sub-slot-avatar">{booking.vendorName?.[0]?.toUpperCase() || "?"}</div>
                                <span className="vw-sub-slot-name">{booking.vendorName}</span>
                              </>
                            ) : (
                              <span className="vw-sub-slot-empty-lbl">{canPick ? "tap to select" : "empty"}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {message && <div className={`vw-message vw-message--${messageType}`}>{message}</div>}

                <div className="vw-modal-actions" style={{ marginTop: 12 }}>
                  {selectedSub ? (
                    <button className="vw-btn vw-btn--primary" onClick={handleConfirmClaim} disabled={busy}>
                      {busy ? "Claiming…" : `Confirm ${activeIsMultiSub ? `${displaySlotLabel}${selectedSub}` : displaySlotLabel} (+KES 7,000)`}
                    </button>
                  ) : (
                    <p className="vw-muted">Pick a side above to claim it.</p>
                  )}
                </div>
              </>
            )}

            <button className="vw-btn vw-btn--ghost" onClick={() => setSelectedSlot(null)} style={{ marginTop: 8 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────
function OverviewTab({ app, event, content }) {
  const steps = getSteps(app);
  const hasLogistics = content.logistics && content.logistics.length > 0;

  return (
    <>
      <div className="vw-card">
        <h3 className="vw-card-title"><Sparkles size={16} /> About {event.name}</h3>
        <p className="vw-story-text">{content.description}</p>
      </div>

      {hasLogistics && (
        <div className="vw-card">
          <h3 className="vw-card-title">Event details</h3>
          <div className="vw-logistics-grid">
            {content.logistics.map((item) => {
              const Icon = item.icon;
              return (
                <div className="vw-stat" key={item.label}>
                  {Icon && <Icon size={16} aria-hidden="true" />}
                  <span>{item.label}</span>
                  <p style={{ fontSize: "12px", textAlign: "center" }}>{item.value}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="vw-card">
        <h3 className="vw-card-title">Onboarding progress</h3>
        <div className="vw-stepper">
          {steps.map((s, i) => (
            <div key={i} className={`vw-step vw-step--${s.state}`}>
              <div className="vw-step-icon">
                {s.state === "done" ? <CheckCircle size={14} /> : s.state === "active" ? <Clock size={14} /> : <div className="vw-step-dot" />}
              </div>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Promote ───────────────────────────────────────────────────────────────
function PromoteTab({ event, content }) {
  const templates = content.socialTemplates || [];

  return (
    <div className="vw-card">
      <h3 className="vw-card-title"><Megaphone size={16} /> Promote your spot</h3>
      <p className="vw-muted">
        Download these, add your logo or booth number, and share them to bring your audience to {event.name}.
      </p>

      {templates.length === 0 ? (
        <p className="vw-placeholder-text">Templates for this event are coming soon — check back closer to the date.</p>
      ) : (
        <div className="vw-template-grid">
          {templates.map((tpl) => (
            <div className="vw-template-card" key={tpl.label}>
              <img src={tpl.imageUrl} alt={tpl.label} className="vw-template-preview" loading="lazy" />
              <div className="vw-template-info">
                <strong>{tpl.label}</strong>
                <span>{tpl.ratio}</span>
              </div>
              <a href={tpl.imageUrl} download className="vw-btn vw-btn--secondary">
                <Download size={14} aria-hidden="true" /> Download
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Layout (read-only viewer) ────────────────────────────────────────────
function LayoutTab({ eventKey, application, mySlotId, layoutItems }) {
  const [subBookings, setSubBookings] = useState({});
  const [reservedSlots, setReservedSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoomIndex, setZoomIndex] = useState(2);
  const ZOOM_LEVELS = [0.5, 0.65, 0.8, 1, 1.25, 1.5];

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("vendor_slots")
      .select("id, slot_id, sub_id, vendor_application_id, tent_mode, reserved, self_selected")
      .eq("event_key", eventKey);

    if (error) { console.error("Slots fetch:", error); setLoading(false); return; }
    if (!data?.length) { setSubBookings({}); setReservedSlots([]); setLoading(false); return; }

    const appIds = [...new Set(data.filter((r) => r.vendor_application_id).map((r) => r.vendor_application_id))];
    let appNames = {};
    if (appIds.length > 0) {
      const { data: apps } = await supabase
        .from("vendor_applications")
        .select("id, vendors(brand_name)")
        .in("id", appIds);
      apps?.forEach((a) => { appNames[a.id] = a.vendors?.brand_name || "Unknown"; });
    }

    const map = {};
    const reserved = [];
    data.forEach((row) => {
      if (row.reserved) { reserved.push(row.slot_id); return; }
      const sub = row.sub_id || "A";
      const key = `${row.slot_id}:${sub}`;
      if (row.vendor_application_id) {
        map[key] = {
          vendorId: row.vendor_application_id,
          vendorName: appNames[row.vendor_application_id] || "Unknown",
          tent_mode: row.tent_mode,
          selfSelected: row.self_selected,
        };
      }
    });
    setSubBookings(map);
    setReservedSlots(reserved);
    setLoading(false);
  }, [eventKey]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const getTileSubBookings = (slotId) => {
    const out = {};
    Object.entries(subBookings).forEach(([k, v]) => {
      if (k.startsWith(slotId + ":")) out[k.split(":")[1]] = v;
    });
    return out;
  };

  const getEffectiveType = (slotConf) => {
    if (reservedSlots.includes(slotConf.id)) return "reserved";
    if (slotConf.type === "20x20") {
      const tileSubs = getTileSubBookings(slotConf.id);
      const entries = Object.entries(tileSubs);
      if (entries.length) {
        const mode = entries[0][1].tent_mode;
        if (mode) return mode;
        const subsPresent = new Set(entries.map(([sub]) => sub));
        return (subsPresent.size > 1 || subsPresent.has("B")) ? "20x20-double" : "20x20-single";
      }
      return "20x20";
    }
    return slotConf.type;
  };

  const getTileCap = (slotConf) => {
    const et = getEffectiveType(slotConf);
    return TENT_SUBDIVISIONS[et]?.subs.length ?? 1;
  };

  const typesInUse = [...new Set(
    layoutItems
      .filter((s) => !["pathway", "blocker"].includes(s.type) && s.id !== "P0")
      .map((s) => getEffectiveType(s))
      .filter((t) => t !== "reserved")
  )];

  const zoomIn = () => setZoomIndex((i) => Math.min(i + 1, ZOOM_LEVELS.length - 1));
  const zoomOut = () => setZoomIndex((i) => Math.max(i - 1, 0));

  const clean = (val) => String(val || "").trim().toLowerCase();
  const activeSlotConfig = selectedSlot
    ? layoutItems.find((s) => clean(s.id) === clean(selectedSlot))
    : null;

  const activeEffType = activeSlotConfig ? getEffectiveType(activeSlotConfig) : null;
  const activeTileSubs = selectedSlot ? getTileSubBookings(selectedSlot) : {};
  const activeIsReserved = selectedSlot ? reservedSlots.includes(selectedSlot) : false;
  const activeDef = activeEffType ? TENT_SUBDIVISIONS[activeEffType] : null;
  const activeIsMultiSub = activeDef && activeDef.subs.length > 1;
  const activeOrientedStyle = activeDef ? getOrientedLayoutStyle(activeDef.layout, activeSlotConfig?.facing) : undefined;

  const displaySlotLabel = activeSlotConfig?.label || selectedSlot;

  return (
    <div className="vw-card vw-card--layout">
      <div className="vw-card-title-row">
        <h3 className="vw-card-title"><MapPin size={16} /> Event layout</h3>
        <button className="vw-icon-btn" onClick={fetchSlots} aria-label="Refresh layout">
          <RefreshCw size={14} />
        </button>
      </div>
      
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
        <p className="vw-muted" style={{ margin: 0 }}>Tap any spot to see who's there. Your spot is outlined in green.</p>
        <span
          className="vw-badge vw-badge--gray"
          style={{
            fontSize: "11px",
            padding: "3px 10px",
            borderRadius: "9999px",
            whiteSpace: "nowrap",
            border: "1px solid rgba(0,0,0,0.08)"
          }}
        >
          Not to scale. Positions are approximate
        </span>
      </div>

      <div className="vw-legend">
        {typesInUse.map((type) => (
          <div key={type} className="vw-legend-item">
            <span className="vw-legend-dot" style={{ background: TENT_COLORS[type] || "#999" }} />
            <span>{TENT_LABELS[type] || type}</span>
          </div>
        ))}
        <div className="vw-legend-item vw-legend-item--dim">
          <span className="vw-legend-dot" style={{ background: TENT_COLORS.reserved }} />
          <span>Reserved</span>
        </div>
        <div className="vw-legend-item vw-legend-item--dim"><span className="vw-legend-dot vw-legend-dot--entry" /><span>Entry</span></div>
        <div className="vw-legend-item vw-legend-item--dim"><span className="vw-legend-dot vw-legend-dot--exit" /><span>Exit</span></div>
        <div className="vw-legend-item vw-legend-item--dim"><span className="vw-legend-dot vw-legend-dot--path" /><span>Path / lane</span></div>
        <div className="vw-legend-item vw-legend-item--dim"><span className="vw-legend-dot vw-legend-dot--facing" /><span>Open front</span></div>
        <div className="vw-legend-item vw-legend-item--dim"><span className="vw-legend-dot vw-legend-dot--mine" /><span>Your spot</span></div>
      </div>

      {loading ? (
        <p className="vw-muted">Loading layout…</p>
      ) : (
        <div className="vw-grid-canvas">
          <div className="vw-zoom-controls">
            <button className="vw-zoom-btn" onClick={zoomOut} disabled={zoomIndex === 0} aria-label="Zoom out">−</button>
            <button className="vw-zoom-btn vw-zoom-btn--label" onClick={() => setZoomIndex(2)}>
              {Math.round(ZOOM_LEVELS[zoomIndex] * 100)}%
            </button>
            <button className="vw-zoom-btn" onClick={zoomIn} disabled={zoomIndex === ZOOM_LEVELS.length - 1} aria-label="Zoom in">+</button>
          </div>

          <div className="vw-grid-scroll">
            <div className="vw-grid-transform" style={{ transform: `scale(${ZOOM_LEVELS[zoomIndex]})` }}>
              <div className="vw-layout-grid">
                {layoutItems.map((slotConf) => {
                  if (slotConf.type === "pathway") return <Pathway key={slotConf.id} slot={slotConf} />;

                  if (slotConf.type === "blocker") {
                    const isEntry = slotConf.id === "Entry";
                    const isExit = slotConf.id === "Exit";
                    return (
                      <div
                        key={slotConf.id}
                        className={[
                          "vw-layout-blocker",
                          isEntry ? "vw-layout-blocker--entry" : "",
                          isExit ? "vw-layout-blocker--exit" : "",
                          !isEntry && !isExit ? "vw-layout-blocker--gap" : "",
                        ].filter(Boolean).join(" ")}
                        style={{ gridColumn: slotConf.gridColumn, gridRow: slotConf.gridRow }}
                      >
                        {isEntry ? "Entry" : isExit ? "Exit" : ""}
                      </div>
                    );
                  }

                  if (slotConf.id === "P0") {
                    return (
                      <div key="P0" className="vw-layout-podium" style={{ gridColumn: slotConf.gridColumn, gridRow: slotConf.gridRow }}>
                        PODIUM
                      </div>
                    );
                  }

                  const et = getEffectiveType(slotConf);
                  const tileSubs = getTileSubBookings(slotConf.id);
                  const used = Object.keys(tileSubs).length;
                  const cap = getTileCap(slotConf);
                  const isRes = reservedSlots.includes(slotConf.id);
                  const iAmHere = clean(mySlotId) === clean(slotConf.id);
                  const isPod = et === "17x17-pod";
                  const hasFacing = slotConf.facing && !isRes;

                  return (
                    <div
                      key={slotConf.id}
                      className={[
                        "vw-layout-tile",
                        iAmHere ? "vw-layout-tile--mine" : "",
                        isPod ? "vw-layout-tile--pod" : "",
                      ].filter(Boolean).join(" ")}
                      style={{
                        gridColumn: slotConf.gridColumn,
                        gridRow: slotConf.gridRow,
                        backgroundColor: iAmHere ? "#00c853" : TENT_COLORS[et] || "#d9d9d9",
                        ...(hasFacing ? getFacingBorders(slotConf.facing) : {}),
                      }}
                      onClick={() => setSelectedSlot(slotConf.id)}
                    >
                      {used > 0 && !isRes && <div className="vw-layout-tile-overlay" style={{ opacity: (used / cap) * 0.35 }} />}
                      <strong className="vw-layout-tile-label">{slotConf.label || slotConf.id}</strong>

                      <TileSubPreview slotId={slotConf.id} subBookings={subBookings} effectiveType={et} facing={slotConf.facing} />

                      {!isPod && used === 1 && !isRes && cap === 1 && (
                        <span className="vw-layout-tile-initial">{Object.values(tileSubs)[0]?.vendorName?.[0]?.toUpperCase()}</span>
                      )}
                      {cap > 1 && !isRes && <small className="vw-layout-tile-count">{used}/{cap}</small>}
                      {iAmHere && <span className="vw-layout-tile-mine">✓</span>}
                      {hasFacing && <FacingArrow facing={slotConf.facing} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSlot && activeSlotConfig && (
        <div className="vw-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setSelectedSlot(null); }}>
          <div className="vw-modal">
            <div className="vw-modal-header">
              <h4>{displaySlotLabel}</h4>
              <button className="vw-modal-close" onClick={() => setSelectedSlot(null)}>✕</button>
            </div>

            {activeIsReserved ? (
              <p className="vw-muted">Reserved by the organiser.</p>
            ) : (
              <>
                <div className="vw-modal-meta">
                  <span
                    className="vw-badge"
                    style={{ background: (TENT_COLORS[activeEffType] || "#999") + "22", color: TENT_COLORS[activeEffType] || "#999" }}
                  >
                    {TENT_LABELS[activeEffType] || activeEffType}
                  </span>
                  {activeSlotConfig.facing && <span className="vw-badge vw-badge--gray">{FACING_LABEL[activeSlotConfig.facing]}</span>}
                </div>

                {activeDef && (
                  <div className="vw-floorplan">
                    <div
                      className={`vw-floorplan-grid vw-floorplan-grid--${activeDef.layout}`}
                      style={activeOrientedStyle}
                    >
                      {activeDef.subs.map((sub) => {
                        const booking = activeTileSubs[sub];
                        const isMe = booking?.vendorId === application?.id;
                        const subLabel = activeIsMultiSub ? `${displaySlotLabel}${sub}` : displaySlotLabel;
                        return (
                          <div
                            key={sub}
                            className={`vw-sub-slot${booking ? " vw-sub-slot--filled" : " vw-sub-slot--empty"}${isMe ? " vw-sub-slot--mine" : ""}`}
                            style={{ background: isMe ? "#00c853" : (TENT_COLORS[activeEffType] || "#ccc") }}
                          >
                            <span className="vw-sub-slot-id">{subLabel}</span>
                            {booking ? (
                              <>
                                <div className="vw-sub-slot-avatar">{booking.vendorName?.[0]?.toUpperCase() || "?"}</div>
                                <span className="vw-sub-slot-name">{isMe ? "You" : booking.vendorName}</span>
                              </>
                            ) : (
                              <span className="vw-sub-slot-empty-lbl">Available</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Documents ────────────────────────────────────────────────────────────
function DocumentsTab({ app, vendor, event, onAppUpdate }) {
  const [uploading, setUploading] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const docs = [
    { key: "indemnity", label: "Indemnity form", path: app.indemnity_path, name: app.indemnity_name },
    { key: "agreement", label: "Vendor agreement", path: app.agreement_path, name: app.agreement_name },
  ];

  const getUrl = (path) => supabase.storage.from(DOCS_BUCKET).getPublicUrl(path).data.publicUrl;

  const handleReplace = async (docKey, file) => {
    if (!file) return;
    setErrorMsg("");
    setUploading(docKey);
    try {
      const safeBrand = (vendor.brand_name || "vendor").replace(/\s+/g, "_").toLowerCase().replace(/[^a-z0-9_-]/g, "");
      const ext = file.name.split(".").pop();
      const path = `${safeBrand}/${event.event_key || event.id}/${docKey}_${Date.now()}.${ext}`;

      const { error: uploadErr } = await supabase.storage.from(DOCS_BUCKET).upload(path, file, { upsert: true });
      if (uploadErr) throw new Error("Upload failed: " + uploadErr.message);

      const pathField = docKey === "indemnity" ? "indemnity_path" : "agreement_path";
      const nameField = docKey === "indemnity" ? "indemnity_name" : "agreement_name";

      const { error: updateErr } = await supabase
        .from("vendor_applications")
        .update({ [pathField]: path, [nameField]: file.name })
        .eq("id", app.id);
      if (updateErr) throw updateErr;

      onAppUpdate((a) => ({ ...a, [pathField]: path, [nameField]: file.name }));
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="vw-card">
      <h3 className="vw-card-title"><FileText size={16} /> Documents</h3>
      <p className="vw-muted">Your signed documents for this event. Replace a file if you need to update it.</p>
      {errorMsg && <p className="vw-error">{errorMsg}</p>}

      <div className="vw-doc-list">
        {docs.map((doc) => (
          <div key={doc.key} className="vw-doc-row">
            <div className="vw-doc-info">
              <FileText size={18} />
              <div>
                <strong>{doc.label}</strong>
                <span>{doc.name || "Not uploaded yet"}</span>
              </div>
            </div>
            <div className="vw-doc-actions">
              {doc.path && (
                <a href={getUrl(doc.path)} target="_blank" rel="noreferrer" className="vw-btn vw-btn--ghost">
                  <Download size={14} /> View
                </a>
              )}
              <label className="vw-btn vw-btn--secondary">
                {uploading === doc.key ? "Uploading…" : doc.path ? "Replace" : "Upload"}
                <input
                  type="file"
                  hidden
                  disabled={uploading === doc.key}
                  onChange={(e) => handleReplace(doc.key, e.target.files[0])}
                />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Extras ────────────────────────────────────────────────────────────────
function ExtrasTab({ app, onAppUpdate, eventKey }) {
  const singleSocketWaived = isExtraWaived("Single Socket", app.tent_type, eventKey);

  const [extras, setExtras] = useState(() => {
    const initial = { ...(app.extras || {}) };
    if (singleSocketWaived) initial["Single Socket"] = 1;
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [saved, setSaved] = useState(false);

  const updateQty = (name, delta) => {
    setSaved(false);
    setExtras((prev) => ({ ...prev, [name]: Math.max(0, (prev[name] || 0) + delta) }));
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg("");
    try {
      const { total, depositRequired } = computeInvoice({
        tentType: app.tent_type,
        customBasePrice: app.custom_base_price,
        extras,
        hasChosenSpot: !!app.chosen_spot,
        eventKey,
      });
      const { error } = await supabase
        .from("vendor_applications")
        .update({ extras, invoice_total: total, deposit_required: depositRequired })
        .eq("id", app.id);
      if (error) throw error;
      onAppUpdate((a) => ({ ...a, extras, invoice_total: total, deposit_required: depositRequired }));
      setSaved(true);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vw-card">
      <h3 className="vw-card-title"><Layers size={16} /> Extras</h3>
      <p className="vw-muted">Adjust quantities below — your invoice total updates when you save.</p>
      {errorMsg && <p className="vw-error">{errorMsg}</p>}

      <div className="vw-extras-list">
        {Object.keys(EXTRA_PRICES).map((name) => {
          const qty = extras[name] || 0;
          const waived = isExtraWaived(name, app.tent_type, eventKey);
          const rawLineTotal = Math.round(EXTRA_PRICES[name] * (waived ? (qty || 1) : qty));
          return (
            <div className="vw-extras-row" key={name}>
              <div className="vw-extras-info">
                <strong>{name}</strong>
                <span>KES {EXTRA_PRICES[name].toLocaleString()} each</span>
                {waived && (
                  <span className="vw-extras-included-note" style={{ display: "block", fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>
                    Included free with your space
                  </span>
                )}
              </div>

              {name === "Single Socket" ? (
                <div className="vw-extras-checkbox">
                  <input
                    type="checkbox"
                    checked={waived ? true : qty === 1}
                    disabled={waived}
                    onChange={(e) => {
                      if (waived) return;
                      setSaved(false);
                      setExtras((prev) => ({
                        ...prev,
                        [name]: e.target.checked ? 1 : 0,
                      }));
                    }}
                  />
                </div>
              ) : (
                <div className="vw-extras-stepper">
                  <button onClick={() => updateQty(name, -1)} disabled={qty <= 0}>−</button>
                  <span>{qty}</span>
                  <button onClick={() => updateQty(name, 1)}>+</button>
                </div>
              )}

              <strong className="vw-extras-line-total">
                {waived ? (
                  <>
                    <span style={{ textDecoration: "line-through", color: "#9ca3af", marginRight: 6 }}>
                      KES {rawLineTotal.toLocaleString()}
                    </span>
                    KES 0
                  </>
                ) : (
                  `KES ${rawLineTotal.toLocaleString()}`
                )}
              </strong>
            </div>
          );
        })}
      </div>

      <button className="vw-btn vw-btn--primary" onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : saved ? "Saved ✓" : "Save changes"}
      </button>
    </div>
  );
}

// ── Invoice ───────────────────────────────────────────────────────────────
function InvoiceTab({ app, vendor, event, total, depositRequired, balance, onAppUpdate, layoutItems }) {
  const [downloading, setDownloading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [payPlan, setPayPlan] = useState("half");
  const [activeModal, setActiveModal] = useState(null);

  const isSponsored = app.tent_type === "sponsored";
  const isITC       = app.tent_type === "ITC";
  const isWfto      = app.tent_type === "WFTO";
  const extras = app.extras || {};

  const eventKeyVal = event.event_key || event.id;

  const live = computeInvoice({
    tentType: app.tent_type,
    customBasePrice: app.custom_base_price,
    extras,
    hasChosenSpot: !!app.chosen_spot,
    eventKey: eventKeyVal,
  });

  const basePrice      = live.basePrice;
  const positioningFee = live.positioningFee;
  const subtotal       = live.subtotal;
  const vatAmount      = live.vatAmount;

  const computedTotal   = app.invoice_total != null ? Math.round(app.invoice_total) : live.total;
  const computedDeposit = app.deposit_required != null ? Math.round(app.deposit_required) : live.depositRequired;

  const depositDeclared = Math.round(
    Number(app.deposit_declared_amount || 0) + Number(app.deposit_topup_declared_amount || 0)
  );
  const depositShortfall = app.deposit_submitted ? Math.max(0, computedDeposit - depositDeclared) : 0;
  const depositSurplus   = app.deposit_submitted ? Math.max(0, depositDeclared - computedDeposit) : 0;

  const computedBalance = depositShortfall > 0
    ? Math.max(0, computedTotal - computedDeposit)
    : Math.max(0, Math.round(computedTotal - depositDeclared));

  const handleDownloadInvoice = async () => {
    setDownloading(true);
    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");
      const invoiceEl = document.getElementById("vw-invoice-box");
      const canvas = await html2canvas(invoiceEl, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const w = 210;
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, w, h);
      pdf.save(`invoice-${(vendor.brand_name || "vendor").replace(/\s+/g, "_")}.pdf`);
    } catch (err) {
      console.error("Invoice download failed:", err);
      setErrorMsg("Couldn't generate the PDF — try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleModalSuccess = (updateFields) => {
    onAppUpdate((a) => ({ ...a, ...updateFields }));
    setActiveModal(null);
  };

  const fullyPaid = app.balance_confirmed;
  const planLocked = app.deposit_submitted || app.balance_submitted;

  const modalRequiredAmount =
    activeModal === "deposit" ? computedDeposit :
    activeModal === "deposit_topup" ? depositShortfall :
    activeModal === "balance" ? computedBalance :
    computedTotal;

  const selectedSpotLabel = app.chosen_spot 
    ? getSlotDisplayLabel(app.chosen_spot, app.previous_sub_id, app.tent_type, layoutItems) 
    : "";

  return (
    <>
      <div className="vw-card">
        <div className="vw-card-title-row">
          <h3 className="vw-card-title"><CreditCard size={16} /> Invoice</h3>
          <button className="vw-btn vw-btn--secondary" onClick={handleDownloadInvoice} disabled={downloading}>
            <Download size={14} /> {downloading ? "Preparing…" : "Download"}
          </button>
        </div>

        <div id="vw-invoice-box" className="vw-invoice-box">
          <div className="vw-invoice-brand">
            <div>
              <h3>{vendor.brand_name}</h3>
              <p>{vendor.email}</p>
            </div>
            <div className="vw-invoice-event-tag">{event?.name}</div>
          </div>

          <div className="vw-invoice-divider" />

          <div className="vw-inv-row">
            <span>
              Base Tent ({TENT_LABELS[app.tent_type] || app.tent_type})
              {isSponsored && <em className="vw-invoice-discount-note"> — Discounted</em>}
              {isITC && <em className="vw-invoice-discount-note"> — ITC Partner</em>}
              {isWfto && <em className="vw-invoice-discount-note"> — WFTO Partner</em>}
            </span>
            <span>KES {basePrice.toLocaleString()}</span>
          </div>

          {Object.entries(extras).map(([key, qty]) => {
            if (!qty) return null;
            const waived = isExtraWaived(key, app.tent_type, eventKeyVal);
            const rawLineTotal = Math.round((EXTRA_PRICES[key] || 0) * qty);
            const lineTotal = waived ? 0 : rawLineTotal;
            return (
              <div className="vw-inv-row" key={key}>
                <span>
                  {key} × {qty}
                  {waived && <em className="vw-invoice-discount-note"> — Waived</em>}
                </span>
                <span>
                  {waived ? (
                    <>
                      <span style={{ textDecoration: "line-through", color: "#9ca3af", marginRight: 6 }}>
                        KES {rawLineTotal.toLocaleString()}
                      </span>
                      KES 0
                    </>
                  ) : (
                    `KES ${lineTotal.toLocaleString()}`
                  )}
                </span>
              </div>
            );
          })}

          {positioningFee > 0 && (
            <div className="vw-inv-row">
              <span>Self-Selected Spot {selectedSpotLabel ? `(${selectedSpotLabel})` : ""}</span>
              <span>KES {POSITIONING_FEE.toLocaleString()}</span>
            </div>
          )}

          <div className="vw-invoice-divider" />

          <div className="vw-inv-row"><span>Subtotal</span><span>KES {subtotal.toLocaleString()}</span></div>
          <div className="vw-inv-row vw-inv-row--muted"><span>VAT (16%)</span><span>KES {Math.round(vatAmount).toLocaleString()}</span></div>

          <div className="vw-invoice-divider" />

          <div className="vw-invoice-total"><span>Total</span><span>KES {computedTotal.toLocaleString()}</span></div>

          <div className="vw-invoice-breakdown">
            <div className="vw-invoice-break-item">
              <span>Deposit (50%)</span>
              <strong>KES {computedDeposit.toLocaleString()}</strong>
            </div>
            <div className="vw-invoice-break-sep">+</div>
            <div className="vw-invoice-break-item">
              <span>Balance</span>
              <strong>KES {computedBalance.toLocaleString()}</strong>
            </div>
          </div>
          {depositSurplus > 0 && (
            <p className="vw-muted" style={{ fontSize: 12, marginTop: 6 }}>
              Balance reduced by KES {depositSurplus.toLocaleString()} carried over from your deposit.
            </p>
          )}
        </div>
      </div>

      <div className="vw-card">
        <h3 className="vw-card-title">Payment methods</h3>
        <div className="vw-payment-grid">
          <div className="vw-payment-card">
            <p className="vw-payment-card-title">🏦 Bank Transfer</p>
            <div className="vw-payment-row"><span>Account Name</span><strong>Biz Baz Events Limited</strong></div>
            <div className="vw-payment-row"><span>Account No.</span><strong>177 027 632 3286</strong></div>
            <div className="vw-payment-row"><span>Bank</span><strong>Equity Bank</strong></div>
            <div className="vw-payment-row"><span>Branch</span><strong>Parliament Road</strong></div>
            <div className="vw-payment-row"><span>Branch Code</span><strong>177</strong></div>
          </div>
          <div className="vw-payment-card">
            <p className="vw-payment-card-title">📱 M-Pesa Paybill</p>
            <div className="vw-payment-row"><span>Paybill</span><strong>247 247</strong></div>
            <div className="vw-payment-row"><span>Account No.</span><strong>826 201</strong></div>
            <div className="vw-payment-row"><span>Account Name</span><strong>Biz Baz Events Limited</strong></div>
          </div>
        </div>
      </div>

      {!fullyPaid && (
        <div className="vw-card">
          <h3 className="vw-card-title"><Upload size={16} /> Submit payment proof</h3>
          {errorMsg && <p className="vw-error">{errorMsg}</p>}

          <div className="vw-pay-plan">
            <button
              type="button"
              className={`vw-pay-plan-option${payPlan === "half" ? " vw-pay-plan-option--active" : ""}`}
              onClick={() => !planLocked && setPayPlan("half")}
              disabled={planLocked && payPlan !== "half"}
            >
              <strong>Pay in two parts</strong>
              <span>Deposit now, balance later</span>
            </button>
            <button
              type="button"
              className={`vw-pay-plan-option${payPlan === "full" ? " vw-pay-plan-option--active" : ""}`}
              onClick={() => !planLocked && setPayPlan("full")}
              disabled={planLocked && payPlan !== "full"}
            >
              <strong>Pay in full</strong>
              <span>KES {computedTotal.toLocaleString()} now</span>
            </button>
          </div>

          {payPlan === "full" ? (
            <div className="vw-upload-row">
              <span>Full amount — KES {computedTotal.toLocaleString()}</span>
              <button className="vw-btn vw-btn--primary" onClick={() => setActiveModal("full")}>
                Upload Full Payment
              </button>
            </div>
          ) : (
            <>
              {!app.deposit_submitted && (
                <div className="vw-upload-row">
                  <span>Deposit — KES {computedDeposit.toLocaleString()}</span>
                  <button className="vw-btn vw-btn--primary" onClick={() => setActiveModal("deposit")}>
                    Upload Deposit
                  </button>
                </div>
              )}

              {app.deposit_submitted && depositShortfall > 0 && (
                <div className="vw-status-pill vw-status-pill--warning">
                  <div>
                    Your space changed and the deposit required is now KES {computedDeposit.toLocaleString()}.
                    You've paid KES {depositDeclared.toLocaleString()} so far — KES {depositShortfall.toLocaleString()} still needed.
                  </div>
                  <button
                    className="vw-btn vw-btn--primary"
                    onClick={() => setActiveModal("deposit_topup")}
                    style={{ marginTop: 8 }}
                  >
                    Upload remaining KES {depositShortfall.toLocaleString()}
                  </button>
                </div>
              )}

              {app.deposit_submitted && depositShortfall === 0 && !app.deposit_confirmed && (
                <div className="vw-status-pill vw-status-pill--pending">
                  Awaiting Verification
                  {depositSurplus > 0 ? (
                    <span> — declared KES {depositDeclared.toLocaleString()} (KES {depositSurplus.toLocaleString()} credited toward your balance)</span>
                  ) : (
                    <span> — declared KES {depositDeclared.toLocaleString()}</span>
                  )}
                </div>
              )}

              {app.deposit_confirmed && !app.balance_submitted && (
                computedBalance <= 0 ? (
                  <div className="vw-status-pill vw-status-pill--done">
                    Fully covered by your deposit — no balance due.
                  </div>
                ) : (
                  <div className="vw-upload-row">
                    <span>
                      Balance — KES {computedBalance.toLocaleString()}
                      {depositSurplus > 0 && (
                        <em className="vw-muted" style={{ display: "block", fontSize: 12 }}>
                          Reduced by KES {depositSurplus.toLocaleString()} deposit overpayment
                        </em>
                      )}
                    </span>
                    <button className="vw-btn vw-btn--primary" onClick={() => setActiveModal("balance")}>
                      Upload Balance
                    </button>
                  </div>
                )
              )}
              {app.balance_submitted && !app.balance_confirmed && (
                <div className="vw-status-pill vw-status-pill--pending">
                  Awaiting Verification
                  {app.balance_declared_amount != null && (
                    <span> — declared KES {Number(app.balance_declared_amount).toLocaleString()}</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeModal && (
        <PaymentUploadModal
          type={activeModal}
          requiredAmount={modalRequiredAmount}
          invoiceTotal={computedTotal}
          application={app}
          vendor={vendor}
          event={event}
          onSuccess={handleModalSuccess}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
}

export default VendorEventWorkspace;